/**
 * The workspace tab strip: Home + one tab per open CALL (tasks never get tabs — they live in
 * the sidebar). Backed by `mine.tabs` (work.user_state.tabs), stable order, persisted.
 *
 *  - a call tab opens /call/:itemId — the curated call workspace (the decision tree window)
 *  - calls are pinned while open; a completed call's tab closes itself
 *  - label: phone number until a customer is identified, then the customer's name
 *  - shows the call's own duration (ringing / live counter / final talk time) and how long
 *    the tab has been open
 *
 * The Home tab is the free-roam CRM; its section nav (Dashboard / Calls / …) is rendered by
 * HomeNav on Home pages only — a call tab fully controls its own window.
 */
import { useEffect, useState } from 'react'
import { NavLink, useLocation, useNavigation } from 'react-router'
import { APP_SECTIONS } from '#app/components/app-shell.tsx'
import { formatPhone } from '#app/components/call-card.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { staffLabel, visibleMatches } from '#app/utils/caller-identity.ts'
import { cn } from '#app/utils/misc.tsx'
import { useOptionalUser, userHasRole } from '#app/utils/user.ts'
import { type WorkItemView } from '#app/utils/work-api.server.ts'
import { useWork } from '#app/utils/work.tsx'

function mmss(seconds: number) {
	const s = Math.max(0, Math.floor(seconds))
	const h = Math.floor(s / 3600)
	const m = Math.floor((s % 3600) / 60)
	const r = s % 60
	return h
		? `${h}:${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`
		: `${m}:${String(r).padStart(2, '0')}`
}

export function tabLabel(item: WorkItemView) {
	const name = item.context?.customer_name
	if (typeof name === 'string' && name.trim()) return name
	// one of our own people: named as such, never as a possible customer
	if (item.caller_identity?.staff)
		return staffLabel(item.caller_identity.staff, {
			short: true,
			direction: item.call?.direction ?? null,
		})
	// the same rule as the banner: an inactive customer never names the tab
	const matches =
		item.caller_identity?.status === 'done'
			? visibleMatches(item.caller_identity.matches)
			: []
	const possible = matches.length === 1 ? matches[0] : null
	if (possible?.name) return `Possible: ${possible.name}`
	if (item.call) return formatPhone(item.call.from_number)
	return item.title
}

/** call duration: ringing/live counting, else final talk time (or rang-only) */
export function callDuration(item: WorkItemView, now: number) {
	const c = item.call
	if (!c) return null
	const end = c.ended_at ? new Date(c.ended_at).getTime() : now
	if (c.answered_at)
		return {
			text: mmss((end - new Date(c.answered_at).getTime()) / 1000),
			live: !c.ended_at,
			ringing: false,
		}
	if (!c.ended_at)
		return {
			text: mmss((now - new Date(c.started_at).getTime()) / 1000),
			live: true,
			ringing: true,
		}
	return { text: 'missed', live: false, ringing: false }
}

export function WorkTabs() {
	const { mine, openCall, goHome } = useWork()
	const location = useLocation()
	const [now, setNow] = useState(() => Date.now())
	const onCallTab = location.pathname.startsWith('/call/')
	const byId = new Map<number, WorkItemView>()
	for (const i of [
		...(mine?.open ?? []),
		...(mine?.active ? [mine.active] : []),
	])
		byId.set(i.id, i)
	const tabs = (mine?.tabs ?? [])
		.map((id) => byId.get(id))
		.filter((i): i is WorkItemView =>
			Boolean(i && i.type === 'call' && i.call?.direction !== 'outbound'),
		)
	useEffect(() => {
		const id = setInterval(() => setNow(Date.now()), 1000)
		return () => clearInterval(id)
	}, [])

	if (!mine) return null

	return (
		// Pinned below the app header (h-14 plus the view-as banner) on every
		// page: a CSR scrolled deep into a list must still see the Home tab and
		// any ringing/live call tabs.
		<div className="sticky top-[calc(3.5rem+var(--banner-h,0px))] z-30 border-b border-black">
			<div className="bg-hep-black-2 border-hep-yellow flex items-end gap-1 overflow-x-auto border-b-2 px-2 pt-1.5 text-sm text-white">
				<TabButton active={!onCallTab} onClick={goHome}>
					<Icon name="home" size="sm" />
					<span className="font-bold">Home</span>
				</TabButton>
				{tabs.map((item) => {
					const isActive = onCallTab && location.pathname === `/call/${item.id}`
					const dur = callDuration(item, now)
					const openFor = mmss(
						(now - new Date(item.created_at).getTime()) / 1000,
					)
					// Active item ⇒ no badge: you're literally looking at it. The server
					// flag stays until a real activation (leaving and reopening the tab
					// logs one) — the client rule covers hand-offs that land while the
					// tab is already active, where no clearable event can be logged.
					const unseen = Boolean(item.assigned_unseen) && !isActive
					return (
						<TabButton
							key={item.id}
							active={isActive}
							onClick={() => (isActive ? undefined : openCall(item))}
							title={`${item.title} — tab open ${openFor}`}
							live={Boolean(dur?.live)}
							unseen={unseen}
						>
							<Icon name="phone-incoming" size="sm" />
							{/* newly assigned to me (missed → manually assigned): a loud
							    marker until the tab is opened, which activates the item and
							    clears the flag server-side */}
							{unseen ? (
								<span
									className="bg-hep-red animate-pulse rounded-full px-1.5 py-0.5 text-[10px] font-black tracking-wide text-white"
									title="newly assigned to you — open to start on it"
								>
									NEW
								</span>
							) : null}
							<span className="max-w-40 truncate font-bold">
								{tabLabel(item)}
							</span>
							{dur ? (
								<span
									className={cn(
										'rounded px-1.5 py-0.5 text-[11px] font-bold tabular-nums',
										dur.live
											? dur.ringing
												? 'bg-sky-500 text-white'
												: 'bg-hep-green text-white'
											: isActive
												? 'bg-black/15'
												: 'bg-white/15',
									)}
									title={
										dur.ringing
											? 'ringing'
											: dur.live
												? 'talk time'
												: 'call length'
									}
								>
									{dur.text}
								</span>
							) : null}
							<span
								className="text-[11px] tabular-nums opacity-70"
								title="tab open for"
							>
								⏱ {openFor}
							</span>
						</TabButton>
					)
				})}
			</div>
			{onCallTab ? null : <HomeNav />}
		</div>
	)
}

/** The CRM's section nav — Home pages only; call tabs control their own window. */
function HomeNav() {
	const user = useOptionalUser()
	const location = useLocation()
	const navigation = useNavigation()
	// highlight where we're GOING during a navigation, not where we were
	const path = navigation.location?.pathname ?? location.pathname
	const sections = APP_SECTIONS.filter(
		(s) => !s.roles || s.roles.some((r) => userHasRole(user ?? null, r)),
	)
	return (
		<nav
			aria-label="Home sections"
			className="bg-card text-card-foreground flex items-center gap-1 overflow-x-auto border-b border-black/20 px-3 py-1.5 dark:border-white/10"
		>
			<span className="text-muted-foreground mr-1 hidden text-[10px] font-bold tracking-[0.2em] uppercase sm:inline">
				Go to
			</span>
			{sections.map((s) => (
				<NavLink
					key={s.to}
					to={s.to}
					prefetch="intent"
					className={() =>
						cn(
							'text-muted-foreground hover:bg-muted hover:text-foreground flex items-center gap-1.5 rounded-full border border-transparent px-3 py-1 text-xs font-bold whitespace-nowrap transition',
							(path === s.to || path.startsWith(s.to + '/')) &&
								'bg-hep-black text-hep-yellow hover:bg-hep-black hover:text-hep-yellow border-black',
						)
					}
				>
					<Icon name={s.icon} size="sm" />
					{s.label}
				</NavLink>
			))}
		</nav>
	)
}

function TabButton({
	active,
	onClick,
	title,
	live,
	unseen,
	children,
}: {
	active: boolean
	onClick: () => void
	title?: string
	live?: boolean
	/** newly assigned to me and not opened yet — the tab itself stands out */
	unseen?: boolean
	children: React.ReactNode
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			title={title}
			className={cn(
				'flex shrink-0 items-center gap-1.5 rounded-t-lg border border-b-0 px-3 py-1.5 transition',
				active
					? 'bg-hep-yellow text-hep-yellow-foreground border-hep-yellow'
					: 'border-white/10 bg-white/5 text-white/85 hover:bg-white/10',
				live && !active && 'border-hep-green/60',
				unseen && !active && 'border-hep-red bg-hep-red/25 hover:bg-hep-red/35',
			)}
		>
			{children}
		</button>
	)
}
